import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { GOAL_TYPE_OPTIONS, STEPPER_BOUNDS, FALLBACK_MINUTES } from '@/constants/reading-tracker';
import { cn } from '@/lib/cn';
import { ApiError } from '@/services/http';
import type { BookAccountMetadataView, GoalType } from '@/types/api';
import { Stepper } from './Stepper';

/**
 * Goal-setup screen — pick a tracking unit + per-day amount.
 *
 * Counts come from `accountMeta` (the EFFECTIVE values: this account's
 * per-book override ?? the admin/catalogue value), NOT directly off the book
 * row. Goal-type availability is per-book: chapters / pages are disabled when
 * the effective count is missing (READING_TRACKER_PLAN §5.5: hide rather than
 * break). The always-editable book-length editor below lets a family fill in
 * or override the counts for their account at any time.
 *
 * The Minutes option used to be the always-available fallback but was
 * removed from the wizard. The `'minutes'` branches in maxFor/STEPPER_
 * BOUNDS are kept for type exhaustiveness over `GoalType`, which still
 * includes `'minutes'` so legacy goals already in the database render
 * correctly elsewhere in the app.
 *
 * The "See My Plan" button is disabled until a goal type is selected.
 * The plan-summary screen reads the selected per-day from the parent's
 * draft state; nothing is computed here on the wire.
 */
export interface BookGoalScreenProps {
  accountMeta: BookAccountMetadataView;
  goalType: GoalType | null;
  perDay: number;
  onGoalTypeChange: (type: GoalType) => void;
  onPerDayChange: (perDay: number) => void;
  onSubmit: () => void;
  onBack: () => void;
  /**
   * Saves this account's page/chapter override (tri-state per field — see
   * `booksService.saveAccountMetadata`). The host updates its `accountMeta`
   * state from the result, which re-renders this picker with the new counts.
   */
  onSaveCounts: (patch: { pages?: number | null; chapters?: number | null }) => Promise<void>;
}

export function BookGoalScreen({
  accountMeta,
  goalType,
  perDay,
  onGoalTypeChange,
  onPerDayChange,
  onSubmit,
  onBack,
  onSaveCounts,
}: BookGoalScreenProps) {
  const availableTypes = useMemo<GoalType[]>(() => {
    const types: GoalType[] = [];
    if (accountMeta.chapters != null) types.push('chapters');
    if (accountMeta.pages != null) types.push('pages');
    // Minutes was previously the always-available fallback; with it
    // removed from the picker, books missing both chapter & page data
    // surface the editable length form below instead of an unworkable wizard.
    return types;
  }, [accountMeta.chapters, accountMeta.pages]);

  const maxFor = (type: GoalType): number => {
    switch (type) {
      case 'chapters':
        return accountMeta.chapters ?? 1;
      case 'pages':
        return accountMeta.pages ?? 1;
      case 'minutes':
        return STEPPER_BOUNDS.minutes.max ?? FALLBACK_MINUTES;
    }
  };

  const stepperConfig = goalType
    ? {
        min: STEPPER_BOUNDS[goalType].min,
        step: STEPPER_BOUNDS[goalType].step,
        max: maxFor(goalType),
      }
    : null;

  return (
    <section className="rounded-3xl border border-or-slate-200 bg-white p-6 shadow-sm">
      <header className="text-center">
        <h2 className="font-display text-[24px] font-bold text-or-slate-900">
          📖 How do you want to track this book?
        </h2>
        <p className="mt-1 text-[13px] text-or-slate-500">
          Choose what makes the most sense for you!
        </p>
      </header>

      <div
        role="radiogroup"
        aria-label="Tracking unit"
        className="mt-6 grid grid-cols-2 gap-3"
      >
        {GOAL_TYPE_OPTIONS.map((opt) => {
          const enabled = availableTypes.includes(opt.type);
          const selected = goalType === opt.type;
          return (
            <button
              key={opt.type}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={!enabled}
              onClick={() => {
                onGoalTypeChange(opt.type);
                // Reset perDay to the type's default when the user switches.
                onPerDayChange(STEPPER_BOUNDS[opt.type].default);
              }}
              className={cn(
                'flex flex-col items-center gap-1 rounded-2xl border-2 p-4 transition-transform',
                'hover:-translate-y-0.5 disabled:hover:translate-y-0',
                'disabled:cursor-not-allowed disabled:opacity-40',
                selected
                  ? 'border-or-orange-600 bg-or-orange-600 text-white'
                  : 'border-or-blue-100 bg-or-cream-100 text-or-slate-700 hover:border-or-orange-600',
              )}
            >
              <span className="text-[26px]" aria-hidden>
                {opt.icon}
              </span>
              <span className="text-[13px] font-bold">{opt.label}</span>
              {!enabled ? (
                <span className="text-[10px] opacity-80">Not available</span>
              ) : null}
            </button>
          );
        })}
      </div>

      <BookLengthEditor accountMeta={accountMeta} onSave={onSaveCounts} />

      {goalType && stepperConfig ? (
        <>
          <hr className="my-6 border-t-2 border-dashed border-or-blue-100" />
          <div className="flex flex-col items-center gap-4">
            <p className="text-center text-[14px] font-bold text-or-slate-700">
              {goalType === 'chapters' && 'How many chapters per day?'}
              {goalType === 'pages' && 'How many pages per day?'}
            </p>
            <Stepper
              value={perDay}
              min={stepperConfig.min}
              max={stepperConfig.max}
              step={stepperConfig.step}
              onChange={onPerDayChange}
              unitLabel={`${goalType} per day`}
              ariaLabel={`${goalType} per day`}
            />
          </div>
        </>
      ) : null}

      <div className="mt-6 flex items-center justify-between">
        <Button type="button" variant="secondary" size="md" onClick={onBack}>
          ← Back
        </Button>
        <Button
          type="button"
          size="lg"
          onClick={onSubmit}
          disabled={!goalType}
        >
          See My Plan! 🗺️
        </Button>
      </div>
    </section>
  );
}

/**
 * Always-editable page/chapter length editor. Prefilled from the EFFECTIVE
 * counts; saving writes to the account's `book_account_metadata` override
 * (never the shared catalogue row). Per-field semantics on save:
 *   * empty input            → clears the override (revert to catalogue).
 *   * value === catalogue     → no override stored (it would be redundant).
 *   * any other value         → stored as this account's override.
 *
 * When an override is in effect we surface a "Reset to catalogue" affordance
 * and show what the catalogue value is. Bounds (`pages` 1–10000, `chapters`
 * 1–2000) mirror the server.
 */
function BookLengthEditor({
  accountMeta,
  onSave,
}: {
  accountMeta: BookAccountMetadataView;
  onSave: (patch: { pages?: number | null; chapters?: number | null }) => Promise<void>;
}) {
  const [pagesInput, setPagesInput] = useState(toInput(accountMeta.pages));
  const [chaptersInput, setChaptersInput] = useState(toInput(accountMeta.chapters));
  const [saving, setSaving] = useState(false);

  // Re-sync the inputs whenever the effective counts change (initial load,
  // a save round-trip, or a reset). Editing in-between is preserved because
  // those edits don't change `accountMeta` until saved.
  useEffect(() => {
    setPagesInput(toInput(accountMeta.pages));
    setChaptersInput(toInput(accountMeta.chapters));
  }, [accountMeta.pages, accountMeta.chapters]);

  const parsedPages = parseField(pagesInput, 10000);
  const parsedChapters = parseField(chaptersInput, 2000);
  const dirty =
    (parsedPages.value ?? null) !== (accountMeta.pages ?? null) ||
    (parsedChapters.value ?? null) !== (accountMeta.chapters ?? null);
  const canSave = dirty && !parsedPages.invalid && !parsedChapters.invalid && !saving;

  const bothMissing = accountMeta.pages == null && accountMeta.chapters == null;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await onSave({
        pages: toOverride(parsedPages.value, accountMeta.global.pages),
        chapters: toOverride(parsedChapters.value, accountMeta.global.chapters),
      });
      toast.success('Book length saved!');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save book length.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-6 rounded-2xl border-2 border-dashed border-or-blue-100 bg-or-cream-100 px-4 py-5">
      <p className="text-center text-[13px] font-bold text-or-slate-700">
        📚 Book length
      </p>
      <p className="mt-1 text-center text-[12px] text-or-slate-500">
        {bothMissing
          ? "We don't have page or chapter info for this book yet — add what you know to start tracking."
          : 'Set your own page or chapter count — saved just for your family.'}
      </p>
      <div className="mt-4 grid grid-cols-2 gap-3">
        <LengthField
          label="Pages"
          value={pagesInput}
          onChange={setPagesInput}
          max={10000}
          placeholder="e.g. 120"
          invalid={parsedPages.invalid}
          overridden={accountMeta.override.pages != null}
          globalValue={accountMeta.global.pages}
          onReset={() => setPagesInput(toInput(accountMeta.global.pages))}
        />
        <LengthField
          label="Chapters"
          value={chaptersInput}
          onChange={setChaptersInput}
          max={2000}
          placeholder="e.g. 12"
          invalid={parsedChapters.invalid}
          overridden={accountMeta.override.chapters != null}
          globalValue={accountMeta.global.chapters}
          onReset={() => setChaptersInput(toInput(accountMeta.global.chapters))}
        />
      </div>
      <div className="mt-4 flex justify-center">
        <Button type="button" size="md" onClick={handleSave} disabled={!canSave}>
          {saving ? 'Saving…' : 'Save book length'}
        </Button>
      </div>
    </div>
  );
}

function LengthField({
  label,
  value,
  onChange,
  max,
  placeholder,
  invalid,
  overridden,
  globalValue,
  onReset,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  max: number;
  placeholder: string;
  invalid: boolean;
  overridden: boolean;
  globalValue: number | null;
  onReset: () => void;
}) {
  return (
    <label className="flex flex-col gap-1 text-[12px] font-semibold text-or-slate-700">
      {label}
      <input
        type="number"
        inputMode="numeric"
        min={1}
        max={max}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={cn(
          'rounded-xl border-2 bg-white px-3 py-2 text-[14px] text-or-slate-900',
          'focus:outline-none focus:ring-2 focus:ring-or-orange-500/40',
          invalid ? 'border-red-400' : 'border-or-blue-100',
        )}
      />
      {overridden ? (
        <span className="flex items-center gap-1 text-[10px] font-medium text-or-slate-500">
          ✏️ Your value
          {globalValue != null ? (
            <>
              {' · '}
              <button
                type="button"
                onClick={onReset}
                className="font-semibold text-or-orange-600 hover:underline"
              >
                Reset to {globalValue}
              </button>
            </>
          ) : null}
        </span>
      ) : globalValue != null ? (
        <span className="text-[10px] font-medium text-or-slate-400">From the catalogue</span>
      ) : null}
    </label>
  );
}

const toInput = (n: number | null) => (n == null ? '' : String(n));

/**
 * Parses a free-text number input into `{ value, invalid }`. Empty is a valid
 * "cleared" state (`value: null, invalid: false`); non-numeric / out-of-range
 * is `invalid: true` so the caller can block save and flag the field.
 */
function parseField(raw: string, max: number): { value: number | null; invalid: boolean } {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { value: null, invalid: false };
  if (!/^\d+$/.test(trimmed)) return { value: null, invalid: true };
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 1 || n > max) return { value: null, invalid: true };
  return { value: n, invalid: false };
}

/**
 * Maps an edited field value to the override payload: a cleared input or a
 * value matching the catalogue means "no override" (`null`); anything else is
 * the account's override.
 */
function toOverride(value: number | null, globalValue: number | null): number | null {
  if (value === null) return null;
  if (value === globalValue) return null;
  return value;
}
